import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { handleError } from "utils/handleError";
import {
  fetchIdeas as fetchIdeasApi,
  createIdea as createIdeaApi,
  updateIdea as updateIdeaApi,
} from "api/idea-rest";

import { CalendarIdeaType, InitialIdeasStateType } from "./types";
import { AccountPlatformType } from "../../shared/types/accounts";

const initialState = {
  isLoading: false,
  ids: [],
  entities: {},
  fetchedGroupIds: [] as string[],
} as InitialIdeasStateType;

export const fetchIdeas = createAsyncThunk<
  { ideas: CalendarIdeaType[]; groupIds: string[] },
  { groupIds: string[] }
>("ideas/fetchIdeas", async (data, { rejectWithValue }) => {
  try {
    const result = await fetchIdeasApi(data);
    const ideas: CalendarIdeaType[] = result.ideas.map((idea) => ({
      _id: idea._id,
      groupId: idea.groupId,
      status: idea.status,
      title: idea.data.title,
      caption: idea.data.caption,
      scheduledAt: idea.data.scheduledAt,
      createdAt: idea.createdAt,
      updatedAt: idea.updatedAt,
      orderIndex: idea.data.orderIndex,
      stage: idea.data.stage,
      labels: idea.data.labels,
      assignees: idea.data.assignees,
      mediaFiles: idea.data.mediaFiles,
      platforms: idea.data.platforms,
      pinnedAt: idea.data.pinnedAt,
      signOff: idea.data.signOff,
      readyToReview: idea.data.readyToReview,
    }));
    return { ideas, groupIds: data.groupIds };
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const createIdea = createAsyncThunk<
  CalendarIdeaType,
  {
    groupId: string;
    title: string;
    scheduledAt: string;
    platforms: AccountPlatformType[];
  }
>("ideas/createIdea", async (data, { rejectWithValue }) => {
  try {
    const result = await createIdeaApi({
      ...data,
      smartCreateType: "title-smart",
    });
    const idea = result.idea;
    return {
      _id: idea._id,
      groupId: idea.data.groupId,
      status: idea.data.status,
      title: idea.data.title,
      caption: idea.data.caption,
      scheduledAt: idea.data.scheduledAt,
      createdAt: new Date(idea.data.createdAt),
      orderIndex: idea.data.orderIndex,
      stage: idea.data.stage,
      labels: idea.data.labels || [],
      assignees: idea.data.assignees || [],
      mediaFiles: idea.data.mediaFiles || [],
      platforms: idea.data.platforms || [],
      pinnedAt: idea.data.pinnedAt,
      signOff: idea.data.signOff,
      readyToReview: idea.data.readyToReview,
    } as CalendarIdeaType;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateIdeaScheduledAt = createAsyncThunk<
  { ideaId: string; scheduledAt: string },
  { ideaId: string; scheduledAt: string }
>("ideas/updateIdeaScheduledAt", async (data, { rejectWithValue }) => {
  try {
    await updateIdeaApi(data);
    return { ideaId: data.ideaId, scheduledAt: data.scheduledAt };
  } catch (error) {
    return rejectWithValue(error);
  }
});

const ideaAdapter = createEntityAdapter({
  sortComparer: (a: CalendarIdeaType, b: CalendarIdeaType) =>
    a?._id?.localeCompare(b?._id),
  selectId: (item: CalendarIdeaType) => item._id || "",
});

export const ideasSlice = createSlice({
  name: "ideas",
  initialState,
  reducers: {
    updateIdeaEntity: (state, action) => {
      ideaAdapter.updateOne(state, action.payload);
    },
    clearIdeas: (state) => {
      ideaAdapter.removeAll(state);
      state.fetchedGroupIds = [];
    },
    setAllIdeas: (
      state,
      action: { payload: { ideas: any[]; groupIds: string[] } }
    ) => {
      ideaAdapter.setAll(state, action.payload.ideas);
      state.fetchedGroupIds = action.payload.groupIds;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchIdeas.fulfilled, (state, action) => {
      ideaAdapter.setAll(state, action.payload.ideas);
      state.fetchedGroupIds = action.payload.groupIds;
      state.isLoading = false;
    });
    builder.addCase(fetchIdeas.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchIdeas.rejected, (state, action) => {
      handleError(action.payload, "Ideas weren't loaded. There was an error.");
      state.isLoading = false;
    });

    builder.addCase(createIdea.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createIdea.fulfilled, (state, action) => {
      toast.success("Idea created successfully");
      ideaAdapter.addOne(state, action.payload);
      state.isLoading = false;
    });
    builder.addCase(
      createIdea.rejected,
      (state, action: { payload: unknown }) => {
        state.isLoading = false;
        handleError(action.payload, "There was an error creating the idea");
      }
    );

    builder.addCase(updateIdeaScheduledAt.fulfilled, (state, action) => {
      ideaAdapter.updateOne(state, {
        id: action.payload.ideaId,
        changes: { scheduledAt: action.payload.scheduledAt },
      });
    });
    builder.addCase(updateIdeaScheduledAt.rejected, (_, action) => {
      handleError(action.payload, "There was an error updating the idea");
    });
  },
});

export const ideasReducer = ideasSlice.reducer;
export const ideasActions = ideasSlice.actions;
